import { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';

function RollbackModal({ snapshot, onClose, onConfirm }) {
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚠️ Confirm Rollback</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="alert-error" style={{ marginBottom: 16 }}>
            <strong>Warning:</strong> Manual entries made after this snapshot point will be lost and cannot be recovered.
          </div>
          <p style={{ fontSize: 14, marginBottom: 16 }}>
            Rolling back to snapshot from <strong>{new Date(snapshot.createdAt).toLocaleString()}</strong>.
          </p>
          <label className="club-checkbox" style={{ gap: 10 }}>
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
            <span style={{ fontSize: 13 }}>I understand that manual entries after this point will be permanently lost.</span>
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={onConfirm} disabled={!confirmed}
            style={{ background: 'var(--red)' }}>
            Rollback
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminRecovery() {
  const { get, post } = useApi();
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [message, setMessage] = useState(null);

  const loadSnapshots = () => {
    setLoading(true);
    get('/recovery').then(d => setSnapshots(d.snapshots || [])).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { loadSnapshots(); }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await post('/recovery', {});
      setMessage({ type: 'success', text: 'Manual snapshot created successfully.' });
      loadSnapshots();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setCreating(false);
    }
  };

  const handleRollback = async () => {
    try {
      await post(`/recovery/${rollbackTarget.id}/restore`, {});
      setMessage({ type: 'success', text: 'Rollback initiated. Data is being restored.' });
      setRollbackTarget(null);
      loadSnapshots();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setRollbackTarget(null);
    }
  };

  const typeLabel = (t) => t === 'auto' ? '⚡ Auto' : '👤 Manual';
  const triggerLabel = (t) => {
    const map = { pre_upload: 'Pre-Upload', nightly: 'Nightly', manual: 'Manual' };
    return map[t] || t;
  };

  return (
    <div>
      {rollbackTarget && <RollbackModal snapshot={rollbackTarget} onClose={() => setRollbackTarget(null)} onConfirm={handleRollback} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
        <div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>
            Snapshots are created automatically before every upload and nightly at 2am.
          </p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Auto snapshots expire after 30 days. Manual snapshots are kept until deleted.
          </p>
        </div>
        <button className="btn-secondary" onClick={handleCreate} disabled={creating}>
          {creating ? 'Creating…' : '+ Manual Snapshot'}
        </button>
      </div>

      {message && (
        <div className={message.type === 'success' ? 'alert-success' : 'alert-error'} style={{ marginBottom: 16 }}>
          {message.text}
        </div>
      )}

      {loading ? <div className="empty-state"><div className="spinner" /></div> : (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Trigger</th>
                <th>Created</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {snapshots.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>No snapshots yet</td></tr>
              )}
              {snapshots.map(s => (
                <tr key={s.id}>
                  <td>{typeLabel(s.type)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{triggerLabel(s.trigger)}</td>
                  <td style={{ fontSize: 12 }}>{new Date(s.createdAt).toLocaleString()}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {s.expiresAt ? new Date(s.expiresAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td>
                    <button className="btn-secondary btn-xs"
                      style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
                      onClick={() => setRollbackTarget(s)}>
                      Rollback
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
